/**
 * Landing Page 2 - With Framer Motion Animations
 * Εντυπωσιακό landing page με scroll animations και 12 sections
 */

import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { buildUrl } from '../lib/seo';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  useTheme,
  useMediaQuery,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import {
  People,
  Schedule,
  Euro,
  Assessment,
  Receipt,
  CloudSync,
  ArrowForward,
  Star,
  KeyboardArrowDown,
  Coffee,
  Restaurant,
  LocalBar,
  Store,
  Hotel,
  Security,
  Verified,
  Cloud,
  Speed,
  ExpandMore,
  Check,
  Close,
  Email,
} from '@mui/icons-material';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';

// Motion components
const MotionBox = motion(Box);
const MotionTypography = motion(Typography);
const MotionCard = motion(Card);
const MotionButton = motion(Button);

// ===========================================
// DATA
// ===========================================

const features = [
  {
    icon: <People sx={{ fontSize: 48 }} />,
    title: 'Διαχείριση Προσωπικού',
    description: 'Καταγραφή υπαλλήλων, θέσεων και ωριαίων αμοιβών με ευκολία.',
    color: '#6366f1',
  },
  {
    icon: <Schedule sx={{ fontSize: 48 }} />,
    title: 'Πρόγραμμα Βαρδιών',
    description: 'Δημιουργία και διαχείριση βαρδιών με drag & drop calendar.',
    color: '#8b5cf6',
  },
  {
    icon: <Euro sx={{ fontSize: 48 }} />,
    title: 'Μισθοδοσία',
    description: 'Αυτόματος υπολογισμός μισθών και καταγραφή πληρωμών.',
    color: '#ec4899',
  },
  {
    icon: <Receipt sx={{ fontSize: 48 }} />,
    title: 'Έσοδα & Έξοδα',
    description: 'Πλήρης καταγραφή εσόδων, γενικών εξόδων και τιμολογίων.',
    color: '#f97316',
  },
  {
    icon: <Assessment sx={{ fontSize: 48 }} />,
    title: 'Οικονομική Εικόνα',
    description: 'Dashboards και reports για πλήρη εικόνα της επιχείρησής σου.',
    color: '#14b8a6',
  },
  {
    icon: <CloudSync sx={{ fontSize: 48 }} />,
    title: 'myDATA Sync',
    description: 'Αυτόματη σύνδεση με το myDATA της ΑΑΔΕ.',
    color: '#3b82f6',
  },
];

const testimonials = [
  {
    name: 'Μαρία Π.',
    role: 'Ιδιοκτήτρια Καφέ',
    text: 'Επιτέλους μια εφαρμογή που καταλαβαίνει τις ανάγκες μιας μικρής επιχείρησης!',
    avatar: '👩‍💼',
  },
  {
    name: 'Γιώργος Κ.',
    role: 'Manager Εστιατορίου',
    text: 'Το πρόγραμμα βαρδιών μου εξοικονομεί τουλάχιστον 2 ώρες την εβδομάδα.',
    avatar: '👨‍💼',
  },
  {
    name: 'Ελένη Μ.',
    role: 'Ιδιοκτήτρια Bar',
    text: 'Η σύνδεση με το myDATA με γλίτωσε από πολύ χαρτοδουλειά!',
    avatar: '👩‍🍳',
  },
];

const howItWorksSteps = [
  {
    number: '01',
    title: 'Δημιούργησε Λογαριασμό',
    description: 'Εγγραφή σε λιγότερο από 1 λεπτό. Δεν χρειάζεται κάρτα.',
    icon: '🚀',
  },
  {
    number: '02',
    title: 'Πρόσθεσε Υπαλλήλους',
    description: 'Καταχώρησε το προσωπικό σου και φτιάξε το πρόγραμμα βαρδιών.',
    icon: '👥',
  },
  {
    number: '03',
    title: 'Παρακολούθησε τα Οικονομικά',
    description: 'Δες έσοδα, έξοδα και κέρδη σε πραγματικό χρόνο.',
    icon: '📊',
  },
];

const targetAudience = [
  { icon: <Coffee sx={{ fontSize: 40 }} />, label: 'Καφετέριες', color: '#8b4513' },
  { icon: <Restaurant sx={{ fontSize: 40 }} />, label: 'Εστιατόρια', color: '#dc2626' },
  { icon: <LocalBar sx={{ fontSize: 40 }} />, label: 'Μπαρ & Clubs', color: '#7c3aed' },
  { icon: <Store sx={{ fontSize: 40 }} />, label: 'Μικρές Επιχειρήσεις', color: '#0891b2' },
  { icon: <Hotel sx={{ fontSize: 40 }} />, label: 'Ξενοδοχεία', color: '#ca8a04' },
];

const stats = [
  { value: 500, suffix: '+', label: 'Επιχειρήσεις', color: '#22c55e' },
  { value: 10000, suffix: '+', label: 'Υπάλληλοι', color: '#6366f1' },
  { value: 1000000, suffix: '+', label: 'Βάρδιες', color: '#ec4899' },
  { value: 98, suffix: '%', label: 'Ικανοποίηση', color: '#f59e0b' },
];

const beforeAfter = [
  { before: 'Excel sheets παντού', after: 'Ένα dashboard για όλα' },
  { before: 'Χαμένες ώρες σε υπολογισμούς', after: 'Αυτόματοι υπολογισμοί μισθών' },
  { before: 'Χαρτοδουλειά & αρχεία', after: 'Digital & Cloud-based' },
  { before: 'Καμία εικόνα οικονομικών', after: 'Real-time reports & insights' },
];

const faqs = [
  {
    question: 'Πώς λειτουργεί η δωρεάν δοκιμή;',
    answer: 'Μπορείς να δοκιμάσεις το Pro plan για 30 ημέρες εντελώς δωρεάν. Δεν χρειάζεται κάρτα. Αν δεν αναβαθμίσεις, θα μεταβείς αυτόματα στο Free plan.',
  },
  {
    question: 'Μπορώ να αλλάξω plan αργότερα;',
    answer: 'Φυσικά! Μπορείς να αναβαθμίσεις ή να υποβαθμίσεις οποιαδήποτε στιγμή. Η αλλαγή είναι άμεση.',
  },
  {
    question: 'Τι συμβαίνει με τα δεδομένα μου αν υποβαθμίσω;',
    answer: 'Τα δεδομένα σου παραμένουν ασφαλή. Αν έχεις περισσότερους από 3 υπαλλήλους, δεν θα μπορείς να προσθέσεις νέους.',
  },
  {
    question: 'Πόσο ασφαλή είναι τα δεδομένα μου;',
    answer: 'Χρησιμοποιούμε 256-bit encryption και daily backups. Τα δεδομένα σου είναι GDPR compliant.',
  },
  {
    question: 'Υπάρχει mobile app;',
    answer: 'Η εφαρμογή είναι πλήρως responsive και λειτουργεί άψογα σε κινητά. Native app έρχεται σύντομα!',
  },
];

const trustBadges = [
  { icon: <Security sx={{ fontSize: 32 }} />, title: '256-bit Encryption', description: 'Κρυπτογράφηση δεδομένων' },
  { icon: <Verified sx={{ fontSize: 32 }} />, title: 'GDPR Compliant', description: 'Συμμόρφωση με EU κανονισμούς' },
  { icon: <Cloud sx={{ fontSize: 32 }} />, title: 'Daily Backups', description: 'Καθημερινά αντίγραφα ασφαλείας' },
  { icon: <Speed sx={{ fontSize: 32 }} />, title: '99.9% Uptime', description: 'Διαθεσιμότητα 24/7' },
];

const partners = [
  'Cafe Nero', 'La Piazza', 'Blue Bar', 'Olive Garden', 'The Coffee House', 'Sunset Lounge'
];

const comparisonFeatures = [
  { feature: 'Διαχείριση Υπαλλήλων', majestify: true, other: true },
  { feature: 'Πρόγραμμα Βαρδιών', majestify: true, other: true },
  { feature: 'Αυτόματη Μισθοδοσία', majestify: true, other: false },
  { feature: 'myDATA Integration', majestify: true, other: false },
  { feature: 'Ελληνική Υποστήριξη', majestify: true, other: false },
  { feature: 'Mobile Friendly', majestify: true, other: true },
  { feature: 'Export Reports', majestify: true, other: false },
  { feature: 'Τιμή', majestify: '€9,90/μήνα', other: '€25+/μήνα' },
];

// ===========================================
// ANIMATION VARIANTS
// ===========================================

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { 
    opacity: 1, 
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

// ===========================================
// COMPONENTS
// ===========================================

// Animated Section Component
function AnimatedSection({ children, variants = fadeInUp, ...props }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <MotionBox
      ref={ref}
      variants={variants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      {...props}
    >
      {children}
    </MotionBox>
  );
}

// Section Title Component
function SectionTitle({ overline, title, subtitle, light = true }) {
  return (
    <AnimatedSection sx={{ textAlign: 'center', mb: 10 }}>
      {overline && (
        <Typography
          variant="overline"
          sx={{ 
            color: '#22c55e', 
            fontWeight: 600, 
            letterSpacing: 3,
            mb: 2,
            display: 'block',
          }}
        >
          {overline}
        </Typography>
      )}
      <Typography
        variant="h2"
        sx={{ 
          fontWeight: 800, 
          mb: 3, 
          color: light ? '#f8fafc' : '#1e293b',
          fontSize: { xs: '2rem', md: '3rem' },
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="h6"
          sx={{ 
            color: light ? '#64748b' : '#64748b', 
            maxWidth: 600, 
            mx: 'auto',
            fontWeight: 400,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </AnimatedSection>
  );
}

// Bento Feature Card with modern hover animation
function BentoFeatureCard({ feature, size = 'normal' }) {
  const isLarge = size === 'large';
  const isWide = size === 'wide';
  
  return (
    <MotionBox
      variants={fadeInUp}
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] }
      }}
      sx={{
        height: '100%',
        minHeight: isLarge ? 320 : isWide ? 200 : 240,
        background: `linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))`,
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 4,
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        p: 0,
        '&:hover': {
          border: `1px solid ${feature.color}40`,
          boxShadow: `0 0 60px ${feature.color}15, inset 0 0 60px ${feature.color}05`,
        },
        '&:hover .feature-icon': {
          transform: 'scale(1.1) rotate(5deg)',
        },
        '&:hover .feature-glow': {
          opacity: 0.15,
        },
        '&:hover .feature-line': {
          width: '100%',
        },
      }}
    >
      {/* Background glow effect */}
      <Box
        className="feature-glow"
        sx={{
          position: 'absolute',
          top: isLarge ? '-20%' : '-30%',
          right: isLarge ? '-10%' : '-20%',
          width: isLarge ? '60%' : '70%',
          height: isLarge ? '60%' : '70%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${feature.color}, transparent 70%)`,
          opacity: 0.08,
          transition: 'opacity 0.5s ease',
          pointerEvents: 'none',
        }}
      />
      
      {/* Content */}
      <Box sx={{ p: isLarge ? 4 : 3, position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <MotionBox
          className="feature-icon"
          sx={{ 
            color: feature.color, 
            mb: isLarge ? 3 : 2,
            display: 'inline-flex',
            p: isLarge ? 2.5 : 2,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${feature.color}20, ${feature.color}05)`,
            border: `1px solid ${feature.color}30`,
            width: 'fit-content',
            transition: 'transform 0.4s ease',
            '& svg': {
              fontSize: isLarge ? 40 : 32,
            }
          }}
        >
          {feature.icon}
        </MotionBox>
        
        <Typography 
          variant={isLarge ? "h4" : "h6"} 
          sx={{ 
            fontWeight: 700, 
            mb: 1.5, 
            color: '#f8fafc',
            fontSize: isLarge ? '1.75rem' : '1.1rem',
          }}
        >
          {feature.title}
        </Typography>
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#94a3b8', 
            lineHeight: 1.7,
            fontSize: isLarge ? '1rem' : '0.875rem',
            flex: 1,
          }}
        >
          {feature.description}
        </Typography>
        
        {/* Animated arrow for large cards */}
        {isLarge && (
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: feature.color, fontWeight: 600, fontSize: '0.875rem' }}>
              Μάθε περισσότερα
            </Typography>
            <ArrowForward sx={{ color: feature.color, fontSize: 18 }} />
          </Box>
        )}
      </Box>
      
      {/* Bottom accent line */}
      <Box
        className="feature-line"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '30%',
          height: '3px',
          background: `linear-gradient(90deg, ${feature.color}, transparent)`,
          transition: 'width 0.5s ease',
        }}
      />
    </MotionBox>
  );
}

// Floating shapes background
function FloatingShapes() {
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {[...Array(6)].map((_, i) => (
        <MotionBox
          key={i}
          animate={{
            y: [0, -30, 0],
            x: [0, 15, 0],
            rotate: [0, 10, 0],
          }}
          transition={{
            duration: 6 + i,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5,
          }}
          sx={{
            position: 'absolute',
            width: 60 + i * 40,
            height: 60 + i * 40,
            borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
            background: `linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))`,
            backdropFilter: 'blur(10px)',
            left: `${10 + i * 15}%`,
            top: `${20 + (i % 3) * 25}%`,
          }}
        />
      ))}
    </Box>
  );
}

// Animated Counter
function AnimatedCounter({ value, suffix = '', duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const end = value;
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += Math.ceil(end / 50);
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(0) + 'K';
    return num.toString();
  };

  return (
    <span ref={ref}>
      {formatNumber(count)}{suffix}
    </span>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function LandingPage2() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  const [email, setEmail] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const pageUrl = buildUrl('/landing');
  const ogImage = buildUrl('/og-image.png');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'Majestify',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web, iOS, Android',
        description: 'Πλατφόρμα διαχείρισης για εστιατόρια, καφετέριες και μπαρ. Προγραμματισμός βαρδιών, μισθοδοσία, έσοδα-έξοδα και σύνδεση με myDATA.',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'EUR',
        },
        featureList: [
          'Διαχείριση προσωπικού και βαρδιών',
          'Αυτόματη μισθοδοσία',
          'Καταγραφή εσόδων και εξόδων',
          'Σύνδεση με myDATA ΑΑΔΕ',
          'Dashboard και reports',
        ],
      },
      {
        '@type': 'Organization',
        name: 'Majestify',
        url: buildUrl('/'),
        logo: ogImage,
      },
    ],
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: '#0f172a',
      overflowX: 'hidden',
    }}>
      <Helmet>
        <title>Majestify – Διαχείριση Εστιατορίου &amp; Προσωπικού | Πρόγραμμα Βαρδιών, Μισθοδοσία, myDATA</title>
        <meta name="description" content="Πλατφόρμα για εστιατόρια, καφετέριες και μπαρ. Προγραμματισμός βαρδιών, μισθοδοσία, έσοδα-έξοδα, σύνδεση myDATA. Ξεκίνα δωρεάν." />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content="Majestify – Διαχείριση Εστιατορίου &amp; Προσωπικού" />
        <meta property="og:description" content="Πλατφόρμα για εστιατόρια, καφετέριες και μπαρ. Προγραμματισμός βαρδιών, μισθοδοσία, έσοδα-έξοδα, myDATA." />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="el_GR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Majestify – Διαχείριση Εστιατορίου &amp; Προσωπικού" />
        <meta name="twitter:description" content="Πλατφόρμα για εστιατόρια, καφετέριες και μπαρ. Προγραμματισμός βαρδιών, μισθοδοσία, myDATA." />
        <meta name="twitter:image" content={ogImage} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      {/* ================================================ */}
      {/* HEADER */}
      {/* ================================================ */}
      <MotionBox
        component="header"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backdropFilter: 'blur(20px)',
          bgcolor: 'rgba(15, 23, 42, 0.8)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <MotionBox
          component="img"
          src="/majestify-logo-white.png"
          alt="Majestify"
          whileHover={{ scale: 1.05 }}
          sx={{
            height: 28,
            cursor: 'pointer',
          }}
        />
        <Stack direction="row" spacing={2} sx={{ display: { xs: 'none', md: 'flex' } }}>
          <MotionButton
            component={RouterLink}
            to="/pricing"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            sx={{ color: '#94a3b8', fontWeight: 500 }}
          >
            Τιμοκατάλογος
          </MotionButton>
          <MotionButton
            component={RouterLink}
            to="/sign-in"
            variant="outlined"
            whileHover={{ scale: 1.05, borderColor: '#22c55e' }}
            whileTap={{ scale: 0.95 }}
            sx={{ 
              borderColor: 'rgba(34, 197, 94, 0.5)', 
              color: '#22c55e',
              borderRadius: 2,
            }}
          >
            Σύνδεση
          </MotionButton>
          <MotionButton
            component={RouterLink}
            to="/sign-up"
            variant="contained"
            whileHover={{ scale: 1.05, boxShadow: '0 20px 40px rgba(34, 197, 94, 0.3)' }}
            whileTap={{ scale: 0.95 }}
            sx={{ 
              background: 'linear-gradient(135deg, #22c55e, #10b981)',
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            Δωρεάν Εγγραφή
          </MotionButton>
        </Stack>
      </MotionBox>

      {/* ================================================ */}
      {/* HERO SECTION */}
      {/* ================================================ */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: '100vh',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
          pt: 10,
        }}
      >
        <FloatingShapes />
        
        {/* Gradient orbs */}
        <Box sx={{ position: 'absolute', top: '20%', left: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34, 197, 94, 0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <Box sx={{ position: 'absolute', bottom: '10%', right: '15%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)', filter: 'blur(60px)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionBox initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
                <Chip
                  label="🎉 30 ημέρες δωρεάν δοκιμή"
                  sx={{
                    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(16, 185, 129, 0.1))',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    color: '#22c55e',
                    fontWeight: 600,
                    mb: 4,
                    py: 2.5,
                  }}
                />
                
                <Typography variant={isMobile ? 'h3' : 'h1'} sx={{ fontWeight: 800, mb: 3, lineHeight: 1.1, color: '#f8fafc' }}>
                  Διαχείριση
                  <br />
                  Επιχείρησης{' '}
                  <Box component="span" sx={{ background: 'linear-gradient(135deg, #22c55e, #10b981, #06b6d4)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}>
                    Απλά
                  </Box>
                </Typography>
                
                <Typography variant="h6" sx={{ mb: 5, color: '#94a3b8', fontWeight: 400, fontSize: '1.25rem', lineHeight: 1.7 }}>
                  Προσωπικό, βάρδιες, μισθοδοσία, έσοδα και έξοδα — όλα σε μία εφαρμογή.
                </Typography>
                
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <MotionButton
                    component={RouterLink}
                    to="/sign-up"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    whileHover={{ scale: 1.05, boxShadow: '0 25px 50px rgba(34, 197, 94, 0.35)' }}
                    whileTap={{ scale: 0.95 }}
                    sx={{ background: 'linear-gradient(135deg, #22c55e, #10b981)', fontWeight: 700, px: 5, py: 2, fontSize: '1.1rem', borderRadius: 3 }}
                  >
                    Ξεκίνα Δωρεάν
                  </MotionButton>
                  <MotionButton
                    component={RouterLink}
                    to="/pricing"
                    variant="outlined"
                    size="large"
                    whileHover={{ scale: 1.05 }}
                    sx={{ borderColor: 'rgba(148, 163, 184, 0.3)', color: '#94a3b8', px: 5, py: 2, borderRadius: 3, '&:hover': { borderColor: '#22c55e', color: '#22c55e' } }}
                  >
                    Δες τα Plans
                  </MotionButton>
                </Stack>
                <Typography sx={{ mt: 2, color: '#64748b', fontSize: '0.9375rem' }}>
                  Ήδη μέλος;{' '}
                  <Box
                    component={RouterLink}
                    to="/sign-in"
                    sx={{
                      color: '#22c55e',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    Σύνδεση
                  </Box>
                </Typography>
              </MotionBox>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <MotionBox
                initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
                animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ duration: 1, delay: 0.6 }}
                whileHover={{ scale: 1.02, rotateY: 5 }}
              >
                <Box sx={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(99, 102, 241, 0.1))', borderRadius: 6, p: 3, border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(20px)', boxShadow: '0 40px 80px rgba(0,0,0,0.3)' }}>
                  <Box sx={{ bgcolor: '#1e293b', borderRadius: 4, p: 3, border: '1px solid rgba(255,255,255,0.05)' }}>
                    <Stack direction="row" spacing={1} mb={3}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f87171' }} />
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fbbf24' }} />
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#22c55e' }} />
                    </Stack>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Υπάλληλοι', value: '12', color: '#6366f1' },
                        { label: 'Βάρδιες', value: '48', color: '#8b5cf6' },
                        { label: 'Μισθοδοσία', value: '€8.240', color: '#22c55e' },
                      ].map((stat, i) => (
                        <Grid item xs={4} key={i}>
                          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 + i * 0.2 }} sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 2, p: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: '#64748b', fontSize: '0.75rem', mb: 0.5 }}>{stat.label}</Typography>
                            <Typography sx={{ color: stat.color, fontWeight: 700, fontSize: '1.25rem' }}>{stat.value}</Typography>
                          </MotionBox>
                        </Grid>
                      ))}
                    </Grid>
                    <Box sx={{ mt: 2, height: 120, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                      {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                        <MotionBox key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 1.5 + i * 0.1, duration: 0.6 }} sx={{ flex: 1, background: 'linear-gradient(180deg, #22c55e, #10b981)', borderRadius: '4px 4px 0 0', opacity: 0.6 + (h / 200) }} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </MotionBox>
            </Grid>
          </Grid>
          
          {/* Scroll indicator */}
          <MotionBox initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} style={{ opacity }} sx={{ position: 'absolute', bottom: 40, left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }}>
            <Typography sx={{ color: '#64748b', mb: 1, fontSize: '0.875rem' }}>Scroll για περισσότερα</Typography>
            <MotionBox animate={{ y: [0, 10, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
              <KeyboardArrowDown sx={{ color: '#22c55e', fontSize: 32 }} />
            </MotionBox>
          </MotionBox>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 1: ΠΩΣ ΛΕΙΤΟΥΡΓΕΙ */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a' }}>
        <Container maxWidth="lg">
          <SectionTitle overline="ΠΩΣ ΛΕΙΤΟΥΡΓΕΙ" title="3 Απλά Βήματα" subtitle="Ξεκίνα σε λιγότερο από 5 λεπτά" />
          
          <MotionBox variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Grid container spacing={4} alignItems="center">
              {howItWorksSteps.map((step, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <MotionBox variants={fadeInUp} sx={{ textAlign: 'center', position: 'relative' }}>
                    {/* Connector line */}
                    {index < howItWorksSteps.length - 1 && !isMobile && (
                      <Box sx={{ position: 'absolute', top: 60, right: -60, width: 120, height: 2, background: 'linear-gradient(90deg, #22c55e, transparent)', display: { xs: 'none', md: 'block' } }} />
                    )}
                    
                    <MotionBox
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.05))',
                        border: '2px solid rgba(34, 197, 94, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        fontSize: '3rem',
                      }}
                    >
                      {step.icon}
                    </MotionBox>
                    <Typography variant="overline" sx={{ color: '#22c55e', fontWeight: 700, letterSpacing: 2 }}>
                      ΒΗΜΑ {step.number}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#f8fafc', my: 2 }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ color: '#64748b', lineHeight: 1.7 }}>
                      {step.description}
                    </Typography>
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 2: ΓΙΑ ΠΟΙΟΥΣ ΕΙΝΑΙ */}
      {/* ================================================ */}
      <Box sx={{ py: 15, background: 'linear-gradient(180deg, #0f172a, #1e293b)' }}>
        <Container maxWidth="lg">
          <SectionTitle overline="ΓΙΑ ΠΟΙΟΥΣ" title="Ιδανικό για κάθε επιχείρηση" subtitle="Από μικρά καφέ μέχρι μεγάλα εστιατόρια" />
          
          <MotionBox variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Grid container spacing={3} justifyContent="center">
              {targetAudience.map((item, index) => (
                <Grid item xs={6} sm={4} md={2.4} key={index}>
                  <MotionBox
                    variants={scaleIn}
                    whileHover={{ scale: 1.1, y: -10 }}
                    sx={{
                      textAlign: 'center',
                      p: 3,
                      borderRadius: 4,
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      '&:hover': {
                        background: `rgba(${item.color}, 0.1)`,
                        borderColor: item.color,
                      }
                    }}
                  >
                    <Box sx={{ color: item.color, mb: 2 }}>{item.icon}</Box>
                    <Typography sx={{ color: '#f8fafc', fontWeight: 600 }}>{item.label}</Typography>
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 3: ΑΡΙΘΜΟΙ ΠΟΥ ΜΙΛΑΝΕ */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a', position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, rgba(34, 197, 94, 0.1), transparent 70%)' }} />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <SectionTitle overline="SOCIAL PROOF" title="Αριθμοί που μιλάνε" />
          
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <AnimatedSection>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, fontWeight: 800, color: stat.color, lineHeight: 1 }}>
                      <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                    </Typography>
                    <Typography sx={{ color: '#64748b', mt: 1, fontSize: '1.1rem' }}>{stat.label}</Typography>
                  </Box>
                </AnimatedSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* FEATURES SECTION - BENTO GRID */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a', position: 'relative', overflow: 'hidden' }}>
        {/* Background decorative elements */}
        <Box sx={{ 
          position: 'absolute', 
          top: '10%', 
          left: '-5%', 
          width: 400, 
          height: 400, 
          borderRadius: '50%', 
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)', 
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: '10%', 
          right: '-5%', 
          width: 350, 
          height: 350, 
          borderRadius: '50%', 
          background: 'radial-gradient(circle, rgba(34, 197, 94, 0.08) 0%, transparent 70%)', 
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Modern Section Header */}
          <AnimatedSection sx={{ textAlign: 'center', mb: 8 }}>
            <MotionBox
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                py: 1,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.05))',
                border: '1px solid rgba(34, 197, 94, 0.2)',
                mb: 4,
              }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e', animation: 'pulse 2s infinite' }} />
              <Typography sx={{ color: '#22c55e', fontWeight: 600, fontSize: '0.875rem', letterSpacing: 1 }}>
                ΔΥΝΑΤΟΤΗΤΕΣ
              </Typography>
            </MotionBox>
            
            <Typography
              variant="h2"
              sx={{ 
                fontWeight: 800, 
                mb: 2, 
                color: '#f8fafc',
                fontSize: { xs: '2rem', md: '3rem' },
                background: 'linear-gradient(135deg, #f8fafc 0%, #94a3b8 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
              }}
            >
              Όλα όσα χρειάζεσαι
            </Typography>
            <Typography
              variant="h6"
              sx={{ 
                color: '#64748b', 
                maxWidth: 500, 
                mx: 'auto',
                fontWeight: 400,
                fontSize: '1.1rem',
              }}
            >
              Μια ολοκληρωμένη σουίτα εργαλείων σχεδιασμένη για την επιχείρησή σου
            </Typography>
          </AnimatedSection>

          {/* Bento Grid Layout */}
          <MotionBox variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Grid container spacing={2.5}>
              {/* Row 1: Large card + 2 normal cards */}
              <Grid item xs={12} md={6}>
                <BentoFeatureCard feature={features[0]} size="large" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6} md={6}>
                    <BentoFeatureCard feature={features[1]} />
                  </Grid>
                  <Grid item xs={12} sm={6} md={6}>
                    <BentoFeatureCard feature={features[2]} />
                  </Grid>
                </Grid>
              </Grid>
              
              {/* Row 2: 3 normal cards */}
              <Grid item xs={12} sm={6} md={4}>
                <BentoFeatureCard feature={features[3]} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <BentoFeatureCard feature={features[4]} />
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                <BentoFeatureCard feature={features[5]} />
              </Grid>
            </Grid>
          </MotionBox>
          
          {/* Bottom CTA */}
          <AnimatedSection sx={{ textAlign: 'center', mt: 8 }}>
            <MotionButton
              component={RouterLink}
              to="/sign-up"
              variant="outlined"
              endIcon={<ArrowForward />}
              whileHover={{ scale: 1.05, borderColor: '#22c55e' }}
              whileTap={{ scale: 0.95 }}
              sx={{ 
                borderColor: 'rgba(148, 163, 184, 0.3)', 
                color: '#94a3b8',
                px: 4,
                py: 1.5,
                borderRadius: 3,
                fontWeight: 600,
                '&:hover': { 
                  borderColor: '#22c55e', 
                  color: '#22c55e',
                  background: 'rgba(34, 197, 94, 0.05)',
                } 
              }}
            >
              Εξερεύνησε όλες τις δυνατότητες
            </MotionButton>
          </AnimatedSection>
        </Container>
        
        {/* CSS for pulse animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </Box>

      {/* ================================================ */}
      {/* SECTION 4: ΠΡΙΝ vs ΜΕΤΑ */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a' }}>
        <Container maxWidth="lg">
          <SectionTitle overline="ΣΥΓΚΡΙΣΗ" title="Πριν vs Μετά το Majestify" />
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <AnimatedSection variants={fadeInLeft}>
                <Box sx={{ p: 4, borderRadius: 4, bgcolor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef4444', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Close /> Πριν
                  </Typography>
                  <Stack spacing={2}>
                    {beforeAfter.map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444' }} />
                        <Typography sx={{ color: '#f8fafc' }}>{item.before}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </AnimatedSection>
            </Grid>
            <Grid item xs={12} md={6}>
              <AnimatedSection variants={fadeInRight}>
                <Box sx={{ p: 4, borderRadius: 4, bgcolor: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#22c55e', mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Check /> Μετά
                  </Typography>
                  <Stack spacing={2}>
                    {beforeAfter.map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
                        <Typography sx={{ color: '#f8fafc' }}>{item.after}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </AnimatedSection>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 7: FAQ ACCORDION */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#1e293b' }}>
        <Container maxWidth="md">
          <SectionTitle overline="FAQ" title="Συχνές Ερωτήσεις" />
          
          <AnimatedSection>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expandedFaq === index}
                onChange={() => setExpandedFaq(expandedFaq === index ? false : index)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '16px !important',
                  mb: 2,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: '0 0 16px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: '#22c55e' }} />}
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontWeight: 600, color: '#f8fafc' }}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: '#94a3b8', lineHeight: 1.7 }}>{faq.answer}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </AnimatedSection>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 8: ΑΣΦΑΛΕΙΑ & TRUST */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a' }}>
        <Container maxWidth="lg">
          <SectionTitle overline="ΑΣΦΑΛΕΙΑ" title="Τα δεδομένα σου είναι ασφαλή" />
          
          <MotionBox variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Grid container spacing={4}>
              {trustBadges.map((badge, index) => (
                <Grid item xs={6} md={3} key={index}>
                  <MotionBox
                    variants={scaleIn}
                    whileHover={{ scale: 1.05 }}
                    sx={{
                      textAlign: 'center',
                      p: 4,
                      borderRadius: 4,
                      bgcolor: 'rgba(34, 197, 94, 0.05)',
                      border: '1px solid rgba(34, 197, 94, 0.2)',
                    }}
                  >
                    <Box sx={{ color: '#22c55e', mb: 2 }}>{badge.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1 }}>
                      {badge.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      {badge.description}
                    </Typography>
                  </MotionBox>
                </Grid>
              ))}
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 9: NEWSLETTER */}
      {/* ================================================ */}
      <Box sx={{ py: 15, background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(99, 102, 241, 0.1))' }}>
        <Container maxWidth="md">
          <AnimatedSection sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#f8fafc', mb: 2 }}>
              Μάθε πρώτος για νέα features
            </Typography>
            <Typography sx={{ color: '#64748b', mb: 4 }}>
              Γράψου στο newsletter μας και λάβε tips για τη διαχείριση της επιχείρησής σου
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ maxWidth: 500, mx: 'auto' }}>
              <TextField
                fullWidth
                placeholder="Το email σου..."
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                InputProps={{
                  startAdornment: <Email sx={{ color: '#64748b', mr: 1 }} />,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 3,
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
                    '&:hover fieldset': { borderColor: '#22c55e' },
                    '& input': { color: '#f8fafc' },
                  },
                }}
              />
              <MotionButton
                variant="contained"
                whileHover={{ scale: 1.05 }}
                sx={{
                  background: 'linear-gradient(135deg, #22c55e, #10b981)',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                Εγγραφή
              </MotionButton>
            </Stack>
          </AnimatedSection>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 10: PARTNERS MARQUEE */}
      {/* ================================================ */}
      <Box sx={{ py: 10, bgcolor: '#0f172a', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: '#64748b', textAlign: 'center', display: 'block', mb: 4, letterSpacing: 3 }}>
            ΜΑΣ ΕΜΠΙΣΤΕΥΟΝΤΑΙ
          </Typography>
          
          <Box sx={{ position: 'relative' }}>
            <MotionBox
              animate={{ x: [0, -1000] }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              sx={{ display: 'flex', gap: 8 }}
            >
              {[...partners, ...partners].map((partner, index) => (
                <Box
                  key={index}
                  sx={{
                    px: 6,
                    py: 3,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: '1.1rem' }}>
                    {partner}
                  </Typography>
                </Box>
              ))}
            </MotionBox>
          </Box>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* SECTION 12: COMPARISON TABLE */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a' }}>
        <Container maxWidth="md">
          <SectionTitle overline="ΣΥΓΚΡΙΣΗ" title="Majestify vs Ανταγωνισμός" />
          
          <AnimatedSection>
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#64748b', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Feature</TableCell>
                    <TableCell align="center" sx={{ color: '#22c55e', fontWeight: 700, borderColor: 'rgba(255,255,255,0.1)' }}>👑 Majestify</TableCell>
                    <TableCell align="center" sx={{ color: '#64748b', fontWeight: 600, borderColor: 'rgba(255,255,255,0.1)' }}>Άλλοι</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonFeatures.map((row, index) => (
                    <TableRow key={index} sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' } }}>
                      <TableCell sx={{ color: '#f8fafc', borderColor: 'rgba(255,255,255,0.1)' }}>{row.feature}</TableCell>
                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                        {typeof row.majestify === 'boolean' ? (
                          row.majestify ? <Check sx={{ color: '#22c55e' }} /> : <Close sx={{ color: '#ef4444' }} />
                        ) : (
                          <Typography sx={{ color: '#22c55e', fontWeight: 700 }}>{row.majestify}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                        {typeof row.other === 'boolean' ? (
                          row.other ? <Check sx={{ color: '#64748b' }} /> : <Close sx={{ color: '#ef4444' }} />
                        ) : (
                          <Typography sx={{ color: '#ef4444' }}>{row.other}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AnimatedSection>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* PRICING PREVIEW */}
      {/* ================================================ */}
      <Box sx={{ py: 15, position: 'relative' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent, rgba(34, 197, 94, 0.05), transparent)' }} />
        <Container maxWidth="md">
          <AnimatedSection variants={scaleIn}>
            <Box sx={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))', backdropFilter: 'blur(20px)', borderRadius: 6, p: { xs: 4, md: 8 }, textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: '-50%', left: '50%', transform: 'translateX(-50%)', width: '200%', height: '100%', background: 'radial-gradient(ellipse, rgba(34, 197, 94, 0.15) 0%, transparent 60%)' }} />
              
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Chip icon={<Star sx={{ color: '#fbbf24 !important' }} />} label="ΠΡΟΣΦΟΡΑ" sx={{ bgcolor: 'rgba(251, 191, 36, 0.1)', color: '#fbbf24', fontWeight: 700, border: '1px solid rgba(251, 191, 36, 0.3)', mb: 4 }} />
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 3, color: '#f8fafc' }}>Pro Plan</Typography>
                
                <Box sx={{ mb: 4 }}>
                  <Typography component="span" sx={{ textDecoration: 'line-through', color: '#64748b', fontSize: '1.5rem', mr: 2 }}>€29,00</Typography>
                  <Typography component="span" sx={{ fontSize: '4rem', fontWeight: 800, background: 'linear-gradient(135deg, #22c55e, #10b981)', backgroundClip: 'text', WebkitBackgroundClip: 'text', color: 'transparent' }}>€9,90</Typography>
                  <Typography component="span" sx={{ color: '#64748b', fontSize: '1.25rem' }}>/μήνα</Typography>
                </Box>
                
                <Chip label="Εξοικονόμηση 66%" sx={{ bgcolor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e', fontWeight: 600, mb: 4, border: '1px solid rgba(34, 197, 94, 0.3)' }} />
                <Typography variant="body1" sx={{ mb: 5, color: '#94a3b8', fontSize: '1.1rem' }}>
                  Απεριόριστοι υπάλληλοι • myDATA Sync • Export Reports • Priority Support
                </Typography>
                
                <MotionButton component={RouterLink} to="/sign-up" variant="contained" size="large" whileHover={{ scale: 1.05, boxShadow: '0 25px 50px rgba(34, 197, 94, 0.35)' }} sx={{ background: 'linear-gradient(135deg, #22c55e, #10b981)', px: 8, py: 2, fontSize: '1.1rem', fontWeight: 700, borderRadius: 3 }}>
                  Δοκίμασε 30 Ημέρες Δωρεάν
                </MotionButton>
              </Box>
            </Box>
          </AnimatedSection>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* TESTIMONIALS */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#1e293b' }}>
        <Container maxWidth="lg">
          <SectionTitle overline="ΜΑΡΤΥΡΙΕΣ" title="Τι λένε οι πελάτες μας" />

          <MotionBox variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <Grid container spacing={4}>
              {testimonials.map((testimonial, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <MotionCard variants={fadeInUp} whileHover={{ y: -8 }} sx={{ height: '100%', background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 4 }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="body1" sx={{ mb: 4, fontStyle: 'italic', color: '#cbd5e1', lineHeight: 1.8 }}>
                        "{testimonial.text}"
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: 3, background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(99, 102, 241, 0.2))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>
                          {testimonial.avatar}
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f8fafc' }}>{testimonial.name}</Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>{testimonial.role}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </MotionCard>
                </Grid>
              ))}
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* FINAL CTA */}
      {/* ================================================ */}
      <Box sx={{ py: 15, bgcolor: '#0f172a' }}>
        <Container maxWidth="md">
          <AnimatedSection sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, color: '#f8fafc', fontSize: { xs: '2rem', md: '3rem' } }}>
              Έτοιμος να ξεκινήσεις;
            </Typography>
            <Typography variant="h6" sx={{ mb: 5, color: '#64748b', fontWeight: 400 }}>
              Δημιούργησε λογαριασμό σε λιγότερο από 1 λεπτό
            </Typography>
            <MotionButton
              component={RouterLink}
              to="/sign-up"
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              whileHover={{ scale: 1.05, boxShadow: '0 25px 50px rgba(34, 197, 94, 0.35)' }}
              sx={{ background: 'linear-gradient(135deg, #22c55e, #10b981)', px: 8, py: 2.5, fontSize: '1.2rem', fontWeight: 700, borderRadius: 3 }}
            >
              Δωρεάν Εγγραφή
            </MotionButton>
          </AnimatedSection>
        </Container>
      </Box>

      {/* ================================================ */}
      {/* FOOTER */}
      {/* ================================================ */}
      <Box component="footer" sx={{ py: 4, textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
          <Box
            component="img"
            src="/majestify-logo-white.png"
            alt="Majestify"
            sx={{
              height: 18,
              opacity: 0.5,
            }}
          />
        </Box>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          © {new Date().getFullYear()} Majestify. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
